import React, { useState } from 'react';

// Define types for our component
interface SqlToPythonProps { }

// Define database types
type ColumnDefinition = {
    name: string;
    type: string;
};

type DatabaseTable = {
    name: string;
    columns: ColumnDefinition[];
    rows: any[][];
};

// Mock database for demo purposes
const mockDatabase: Record<string, DatabaseTable> = {
    users: {
        name: 'users',
        columns: [
            { name: 'id', type: 'number' },
            { name: 'name', type: 'string' },
            { name: 'email', type: 'string' },
            { name: 'created_at', type: 'date' }
        ],
        rows: [
            [1, 'John Doe', 'john@example.com', '2023-01-15'],
            [2, 'Jane Smith', 'jane@example.com', '2023-02-20'],
            [3, 'Bob Johnson', 'bob@example.com', '2023-03-10'],
            [4, 'Alice Brown', 'alice@example.com', '2023-04-05'],
            [5, 'Charlie Davis', 'charlie@example.com', '2023-05-12']
        ]
    },
    products: {
        name: 'products',
        columns: [
            { name: 'id', type: 'number' },
            { name: 'name', type: 'string' },
            { name: 'price', type: 'number' },
            { name: 'category', type: 'string' },
            { name: 'in_stock', type: 'boolean' }
        ],
        rows: [
            [101, 'Laptop', 1299.99, 'Electronics', true],
            [102, 'Smartphone', 899.99, 'Electronics', true],
            [103, 'Headphones', 199.99, 'Electronics', false],
            [104, 'Desk Chair', 249.99, 'Furniture', true],
            [105, 'Coffee Table', 349.99, 'Furniture', true],
            [106, 'Bookshelf', 199.99, 'Furniture', false],
            [107, 'Water Bottle', 24.99, 'Kitchen', true],
            [108, 'Blender', 89.99, 'Kitchen', true]
        ]
    },
    orders: {
        name: 'orders',
        columns: [
            { name: 'id', type: 'number' },
            { name: 'user_id', type: 'number' },
            { name: 'total', type: 'number' },
            { name: 'status', type: 'string' },
            { name: 'order_date', type: 'date' }
        ],
        rows: [
            [1001, 1, 1299.99, 'Delivered', '2023-06-15'],
            [1002, 2, 899.99, 'Shipped', '2023-06-20'],
            [1003, 3, 249.99, 'Processing', '2023-06-25'],
            [1004, 1, 89.99, 'Delivered', '2023-07-01'],
            [1005, 4, 549.98, 'Shipped', '2023-07-05'],
            [1006, 5, 199.99, 'Processing', '2023-07-10']
        ]
    }
};

// Types for SQL parsing results
type ParsedSql = {
    tableName: string;
    selectedColumns: string[];
    whereClause: string | null;
    orderByClause: string | null;
    limitClause: number | null;
};

// Simple SQL parser function
const parseSql = (sqlQuery: string): ParsedSql => {
    sqlQuery = sqlQuery.trim();

    // Basic SELECT statement parsing
    if (sqlQuery.toLowerCase().startsWith('select')) {
        // Very basic parsing - not a production-ready SQL parser
        const selectMatch = sqlQuery.match(/select\s+(.*?)\s+from\s+(\w+)(?:\s+where\s+(.*?))?(?:\s+order\s+by\s+(.*?))?(?:\s+limit\s+(\d+))?;?\s*$/i);

        if (!selectMatch) {
            throw new Error('Invalid SQL query format. Expected: SELECT columns FROM table [WHERE condition] [ORDER BY columns] [LIMIT number]');
        }

        const columns = selectMatch[1].trim();
        const tableName = selectMatch[2].trim();
        const whereClause = selectMatch[3] ? selectMatch[3].trim() : null;
        const orderByClause = selectMatch[4] ? selectMatch[4].trim() : null;
        const limitClause = selectMatch[5] ? parseInt(selectMatch[5].trim()) : null;

        // Check if table exists
        if (!mockDatabase[tableName]) {
            throw new Error(`Table '${tableName}' does not exist`);
        }

        // Parse columns
        let selectedColumns: string[] = [];

        if (columns === '*') {
            // Select all columns
            selectedColumns = mockDatabase[tableName].columns.map(col => col.name);
        } else {
            // Select specific columns
            selectedColumns = columns.split(',').map(col => col.trim());

            // Validate that all columns exist
            selectedColumns.forEach(colName => {
                const colExists = mockDatabase[tableName].columns.some(col => col.name === colName);
                if (!colExists) {
                    throw new Error(`Column '${colName}' does not exist in table '${tableName}'`);
                }
            });
        }

        return {
            tableName,
            selectedColumns,
            whereClause,
            orderByClause,
            limitClause
        };
    } else {
        throw new Error('Only SELECT queries are supported');
    }
};

// Function to convert SQL to Pandas code
const sqlToPandas = (parsedSql: ParsedSql): string => {
    const { tableName, selectedColumns, whereClause, orderByClause, limitClause } = parsedSql;
    
    let pythonCode = `import pandas as pd\n\n`;
    pythonCode += `# Load data from ${tableName}\n`;
    pythonCode += `${tableName} = pd.read_csv('${tableName}.csv')  # Replace with your actual data source\n\n`;
    
    pythonCode += `# Perform the query\n`;
    
    // Start with selection
    const columnsStr = selectedColumns.map(col => `'${col}'`).join(', ');
    
    if (selectedColumns.length === mockDatabase[tableName].columns.length && selectedColumns[0] === mockDatabase[tableName].columns[0].name) {
        // If selecting all columns, we can just start with the dataframe
        pythonCode += `result = ${tableName}`;
    } else {
        // Selecting specific columns
        pythonCode += `result = ${tableName}[[${columnsStr}]]`;
    }
    
    // Apply WHERE clause if it exists
    if (whereClause) {
        // Convert SQL condition to pandas syntax
        const pandasCondition = convertSqlWhereToPandas(whereClause, tableName);
        pythonCode += `.query("${pandasCondition}")`;
    }
    
    // Apply ORDER BY if it exists
    if (orderByClause) {
        const parts = orderByClause.split(' ');
        const column = parts[0];
        const direction = parts.length > 1 && parts[1].toLowerCase() === 'desc' ? false : true;
        
        pythonCode += `\nresult = result.sort_values(by='${column}', ascending=${direction})`;
    }
    
    // Apply LIMIT if it exists
    if (limitClause !== null) {
        pythonCode += `\nresult = result.head(${limitClause})`;
    }
    
    // Add displaying the results
    pythonCode += `\n\n# Display the results\nprint(result)`;
    
    return pythonCode;
};

// Function to convert SQL to SQLAlchemy code
const sqlToSqlAlchemy = (parsedSql: ParsedSql): string => {
    const { tableName, selectedColumns, whereClause, orderByClause, limitClause } = parsedSql;
    
    let pythonCode = `from sqlalchemy import create_engine, MetaData, Table, select, column, and_, or_, desc\n`;
    pythonCode += `from sqlalchemy.orm import Session\n\n`;
    
    pythonCode += `# Create engine and connect to your database\n`;
    pythonCode += `engine = create_engine('sqlite:///your_database.db')  # Replace with your actual connection string\n`;
    pythonCode += `metadata = MetaData()\n`;
    pythonCode += `conn = engine.connect()\n\n`;
    
    // Define the table
    pythonCode += `# Define table structure\n`;
    pythonCode += `${tableName} = Table('${tableName}', metadata, autoload_with=engine)\n\n`;
    
    // Build the query
    pythonCode += `# Build the query\n`;
    
    if (selectedColumns[0] === mockDatabase[tableName].columns[0].name && 
        selectedColumns.length === mockDatabase[tableName].columns.length) {
        // If selecting all columns
        pythonCode += `query = select(${tableName})`;
    } else {
        // Selecting specific columns
        const columnsStr = selectedColumns.map(col => `${tableName}.c.${col}`).join(', ');
        pythonCode += `query = select(${columnsStr})`;
    }
    
    // Apply WHERE clause if it exists
    if (whereClause) {
        const sqlAlchemyCondition = convertSqlWhereToSqlAlchemy(whereClause, tableName);
        pythonCode += `.where(${sqlAlchemyCondition})`;
    }
    
    // Apply ORDER BY if it exists
    if (orderByClause) {
        const parts = orderByClause.split(' ');
        const column = parts[0];
        
        if (parts.length > 1 && parts[1].toLowerCase() === 'desc') {
            pythonCode += `.order_by(desc(${tableName}.c.${column}))`;
        } else {
            pythonCode += `.order_by(${tableName}.c.${column})`;
        }
    }
    
    // Apply LIMIT if it exists
    if (limitClause !== null) {
        pythonCode += `.limit(${limitClause})`;
    }
    
    // Execute the query and print results
    pythonCode += `\n\n# Execute the query\nresult = conn.execute(query)\n\n`;
    pythonCode += `# Fetch and display all results\nfor row in result:\n    print(row)`;
    
    return pythonCode;
};

// Helper function to convert SQL WHERE clause to pandas query syntax
const convertSqlWhereToPandas = (whereClause: string, tableName: string): string => {
    // This is a very simplistic conversion and doesn't handle complex WHERE clauses
    // For a production-ready solution, a proper SQL parser would be needed
    
    // Replace SQL operators with Python operators
    let pandasCondition = whereClause
        .replace(/=/g, '==')
        .replace(/<>/g, '!=')
        .replace(/AND/gi, '&')
        .replace(/OR/gi, '|');
    
    // Handle string literals
    pandasCondition = pandasCondition.replace(/'([^']*)'/g, '"$1"');
    pandasCondition = pandasCondition.replace(/"([^"]*)"/g, '"$1"');
    
    return pandasCondition;
};

// Helper function to convert SQL WHERE clause to SQLAlchemy syntax
const convertSqlWhereToSqlAlchemy = (whereClause: string, tableName: string): string => {
    // This is a very simplistic conversion and doesn't handle complex WHERE clauses
    // For a production-ready solution, a proper SQL parser would be needed
    
    // Check for simple equality condition
    const equalityMatch = whereClause.match(/(\w+)\s*=\s*(['"].*?['"]|\d+|\w+)/);
    if (equalityMatch) {
        const column = equalityMatch[1];
        let value = equalityMatch[2];
        
        // If value is a string with quotes, keep the quotes
        if (!(value.startsWith("'") || value.startsWith('"'))) {
            // Check if it's a number
            if (!isNaN(Number(value))) {
                // It's a number, no quotes needed
            } else {
                // It's likely a column or variable, reference it as such
                value = `${tableName}.c.${value}`;
            }
        }
        
        return `${tableName}.c.${column} == ${value}`;
    }
    
    // Check for inequality condition
    const inequalityMatch = whereClause.match(/(\w+)\s*(<>|!=)\s*(['"].*?['"]|\d+|\w+)/);
    if (inequalityMatch) {
        const column = inequalityMatch[1];
        let value = inequalityMatch[3];
        
        if (!(value.startsWith("'") || value.startsWith('"'))) {
            if (!isNaN(Number(value))) {
                // It's a number
            } else {
                value = `${tableName}.c.${value}`;
            }
        }
        
        return `${tableName}.c.${column} != ${value}`;
    }
    
    // Check for greater than condition
    const greaterMatch = whereClause.match(/(\w+)\s*>\s*(['"].*?['"]|\d+|\w+)/);
    if (greaterMatch) {
        const column = greaterMatch[1];
        let value = greaterMatch[2];
        
        if (!(value.startsWith("'") || value.startsWith('"'))) {
            if (!isNaN(Number(value))) {
                // It's a number
            } else {
                value = `${tableName}.c.${value}`;
            }
        }
        
        return `${tableName}.c.${column} > ${value}`;
    }
    
    // Check for less than condition
    const lessMatch = whereClause.match(/(\w+)\s*<\s*(['"].*?['"]|\d+|\w+)/);
    if (lessMatch) {
        const column = lessMatch[1];
        let value = lessMatch[2];
        
        if (!(value.startsWith("'") || value.startsWith('"'))) {
            if (!isNaN(Number(value))) {
                // It's a number
            } else {
                value = `${tableName}.c.${value}`;
            }
        }
        
        return `${tableName}.c.${column} < ${value}`;
    }
    
    // If we can't parse it, return as is and let SQLAlchemy handle it
    return whereClause;
};

// Main component to convert SQL to Python
const SqlToPython: React.FC<SqlToPythonProps> = () => {
    // State for the SQL input
    const [sqlInput, setSqlInput] = useState<string>('');
    // State for the Python output
    const [pythonOutput, setPythonOutput] = useState<string>('');
    // State for error messages
    const [error, setError] = useState<string>('');
    // State for the file name
    const [fileName, setFileName] = useState<string>('sql_to_python');
    // State for loading indicator
    const [isLoading, setIsLoading] = useState<boolean>(false);
    // State for showing database schema
    const [showSchema, setShowSchema] = useState<boolean>(false);
    // State to track if the conversion happened
    const [isPythonGenerated, setIsPythonGenerated] = useState<boolean>(false);
    // State for copied indicator
    const [copied, setCopied] = useState<boolean>(false);
    
    // Python code generation options
    const [pythonFramework, setPythonFramework] = useState<'pandas' | 'sqlalchemy'>('pandas');
    const [includeComments, setIncludeComments] = useState<boolean>(true);

    // Handle SQL input change
    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setSqlInput(e.target.value);
        setError('');
        setIsPythonGenerated(false);
    };

    // Use example query
    const useExampleQuery = () => {
        const exampleQuery = 'SELECT * FROM products WHERE category = "Electronics";';
        setSqlInput(exampleQuery);
        setError('');
        setIsPythonGenerated(false);
        setPythonOutput('');
    };

    // Convert SQL to Python
    const convertSqlToPython = () => {
        setError('');
        setIsLoading(true);
        setIsPythonGenerated(false);
        setPythonOutput('');

        if (!sqlInput.trim()) {
            setError('Please enter an SQL query');
            setIsLoading(false);
            return;
        }

        try {
            // Parse the SQL query
            const parsedSql = parseSql(sqlInput);

            // Generate the Python code based on selected framework
            let pythonCode: string;
            
            if (pythonFramework === 'pandas') {
                pythonCode = sqlToPandas(parsedSql);
            } else {
                pythonCode = sqlToSqlAlchemy(parsedSql);
            }
            
            // Remove comments if not wanted
            if (!includeComments) {
                pythonCode = pythonCode.split('\n')
                    .filter(line => !line.trim().startsWith('#'))
                    .join('\n');
            }
            
            setPythonOutput(pythonCode);
            setIsPythonGenerated(true);
            setIsLoading(false);
        } catch (e) {
            setError(`Error converting SQL to Python: ${e instanceof Error ? e.message : String(e)}`);
            setIsLoading(false);
        }
    };

    // Clear all inputs and reset states
    const clearAll = () => {
        setSqlInput('');
        setPythonOutput('');
        setError('');
        setFileName('sql_to_python');
        setIsPythonGenerated(false);
    };

    // Copy Python to clipboard
    const copyToClipboard = () => {
        navigator.clipboard.writeText(pythonOutput)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            })
            .catch(() => {
                setError('Failed to copy to clipboard');
            });
    };

    // Download Python file
    const downloadPython = () => {
        const blob = new Blob([pythonOutput], { type: 'text/plain;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = `${fileName || 'sql_to_python'}.py`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // Render database schema
    const renderDatabaseSchema = () => {
        return (
            <div className="bg-blue-50 p-4 rounded-md border border-blue-100 text-sm">
                <h3 className="font-medium mb-2">Database Schema</h3>
                {Object.values(mockDatabase).map(table => (
                    <div key={table.name} className="mb-3">
                        <div className="font-medium">{table.name}</div>
                        <div className="pl-4">
                            {table.columns.map((col, index) => (
                                <div key={index}>
                                    {col.name} ({col.type})
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
                <p className="mt-3 text-blue-800">
                    This is a mock database for demonstration. In a real application, you would
                    connect to an actual database or database API.
                </p>
            </div>
        );
    };

    return (
        <div className="max-w-6xl mx-auto">
            <h1 className="text-2xl font-bold mb-6">SQL to Python Converter</h1>

            {/* Description */}
            <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
                <p className="text-blue-800">
                    Convert SQL queries to equivalent Python code using pandas or SQLAlchemy. This tool helps data scientists and developers quickly translate their SQL knowledge to Python code.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Input Section */}
                <div className="flex flex-col h-full">
                    <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
                        <div className="flex justify-between items-center mb-3">
                            <label htmlFor="sqlInput" className="font-medium text-gray-700">
                                SQL Query
                            </label>
                            <div className="flex space-x-2">
                                <button
                                    className="text-sm text-blue-600 hover:text-blue-800"
                                    onClick={useExampleQuery}
                                >
                                    Use Example
                                </button>
                                <button
                                    className="text-sm text-gray-600 hover:text-gray-800"
                                    onClick={() => setSqlInput('')}
                                >
                                    Clear
                                </button>
                            </div>
                        </div>

                        <textarea
                            id="sqlInput"
                            className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                            value={sqlInput}
                            onChange={handleInputChange}
                            placeholder="Enter your SQL query here, e.g.,
SELECT * FROM users WHERE created_at > '2023-01-01';"
                        />

                        <div className="mt-3">
                            <button
                                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                onClick={() => setShowSchema(!showSchema)}
                            >
                                <span>{showSchema ? 'Hide' : 'Show'} Database Schema</span>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className={`h-4 w-4 ml-1 transition-transform ${showSchema ? 'transform rotate-180' : ''}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                        </div>

                        {showSchema && (
                            <div className="mt-3">
                                {renderDatabaseSchema()}
                            </div>
                        )}
                    </div>

                    {/* Configuration */}
                    <div className="bg-white p-4 rounded-md shadow-sm">
                        <div className="mb-4">
                            <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                                Output File Name
                            </label>
                            <input
                                type="text"
                                id="fileName"
                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                value={fileName}
                                onChange={(e) => setFileName(e.target.value)}
                                placeholder="Enter file name (without extension)"
                            />
                        </div>

                        <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                            <div>
                                <label htmlFor="pythonFramework" className="block text-sm font-medium text-gray-700 mb-1">
                                    Python Framework
                                </label>
                                <select
                                    id="pythonFramework"
                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                    value={pythonFramework}
                                    onChange={(e) => setPythonFramework(e.target.value as 'pandas' | 'sqlalchemy')}
                                >
                                    <option value="pandas">pandas (Data Analysis)</option>
                                    <option value="sqlalchemy">SQLAlchemy (ORM)</option>
                                </select>
                            </div>

                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="includeComments"
                                    checked={includeComments}
                                    onChange={(e) => setIncludeComments(e.target.checked)}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label htmlFor="includeComments" className="ml-2 block text-sm text-gray-700">
                                    Include comments in generated code
                                </label>
                            </div>
                        </div>

                        <div className="mt-4 flex justify-between">
                            <button
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                onClick={convertSqlToPython}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <div className="flex items-center">
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Processing...
                                    </div>
                                ) : (
                                    'Generate Python'
                                )}
                            </button>

                            <button
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                                onClick={clearAll}
                            >
                                Clear All
                            </button>
                        </div>
                    </div>
                </div>

                {/* Output Section */}
                <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
                    <div className="flex justify-between items-center mb-3">
                        <label className="font-medium text-gray-700">
                            Python Code
                        </label>

                        {isPythonGenerated && (
                            <div className="flex space-x-2">
                                <button
                                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                    onClick={copyToClipboard}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-4 w-4 mr-1"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                    {copied ? 'Copied!' : 'Copy Code'}
                                </button>

                                <button
                                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                    onClick={downloadPython}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-4 w-4 mr-1"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                    </svg>
                                    Download .py
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="flex-grow flex flex-col">
                        {error && (
                            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                                <p className="font-medium">Error</p>
                                <p>{error}</p>
                            </div>
                        )}

                        {!error && !isPythonGenerated && (
                            <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                                <div className="text-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    <p>
                                        Python code will appear here after converting the SQL query.
                                    </p>
                                </div>
                            </div>
                        )}

                        {!error && isPythonGenerated && (
                            <div className="flex-grow flex flex-col">
                                <div className="flex-grow flex flex-col">
                                    <textarea
                                        id="pythonOutput"
                                        className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                                        value={pythonOutput}
                                        readOnly
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {isPythonGenerated && (
                        <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
                            <div className="flex items-center mb-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <p className="font-medium">SQL Successfully Converted to Python!</p>
                            </div>
                            <p className="text-sm">
                                You can now copy or download the generated Python code. This code can be used in your data analysis, ETL processes, or web applications.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Information Section */}
            <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
                <h2 className="text-lg font-semibold mb-4">About SQL to Python Conversion</h2>

                <div className="space-y-4">
                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">What This Tool Does</h3>
                        <p className="text-gray-600">
                            This SQL to Python converter helps you translate SQL queries into Python code using popular frameworks like pandas and SQLAlchemy. This is particularly useful for data scientists, analysts, and developers who are transitioning from SQL-based workflows to Python-based data processing pipelines.
                        </p>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">Features</h3>
                        <ul className="list-disc pl-5 text-gray-600">
                            <li><strong>SQL Query Parsing</strong> - Parse and analyze SELECT queries</li>
                            <li><strong>Multiple Python Frameworks</strong> - Convert to either pandas or SQLAlchemy code</li>
                            <li><strong>Code Comments</strong> - Include optional explanatory comments in the generated code</li>
                            <li><strong>Database Schema View</strong> - View the available tables and columns</li>
                            <li><strong>Python File Download</strong> - Download results as a .py file</li>
                            <li><strong>Copy to Clipboard</strong> - Easily copy the Python code</li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">Python Framework Options</h3>
                        <ul className="list-disc pl-5 text-gray-600">
                            <li>
                                <strong>pandas</strong> - Great for data analysis and manipulation:
                                <pre className="bg-gray-50 p-2 mt-1 rounded-md font-mono text-xs overflow-auto">
                                    import pandas as pd&#10;&#10;# Load data&#10;data = pd.read_csv('table.csv')&#10;&#10;# Query&#10;result = data[data['category'] == 'Electronics']
                                </pre>
                            </li>
                            <li>
                                <strong>SQLAlchemy</strong> - ORM for database interactions in Python applications:
                                <pre className="bg-gray-50 p-2 mt-1 rounded-md font-mono text-xs overflow-auto">
                                    from sqlalchemy import create_engine, Table, select, MetaData&#10;&#10;engine = create_engine('connection_string')&#10;metadata = MetaData()&#10;&#10;# Define table&#10;table = Table('table', metadata, autoload_with=engine)&#10;&#10;# Query&#10;query = select(table).where(table.c.category == 'Electronics')
                                </pre>
                            </li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">Limitations of This Demo</h3>
                        <ul className="list-disc pl-5 text-gray-600">
                            <li>Uses a mock in-memory database with predefined data</li>
                            <li>Only supports basic SELECT queries with simple WHERE clauses</li>
                            <li>Limited SQL syntax support (no JOINs, GROUP BY, HAVING, etc.)</li>
                            <li>Simple SQL-to-Python translation that may need refinement for complex cases</li>
                            <li>No support for parameterized queries</li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">Supported SQL Features</h3>
                        <ul className="list-disc pl-5 text-gray-600">
                            <li><strong>SELECT columns</strong> - Specify columns to select, or use * for all columns</li>
                            <li><strong>FROM table</strong> - Select data from available tables (users, products, orders)</li>
                            <li><strong>WHERE condition</strong> - Basic comparison operators </li>
                            <li><strong>ORDER BY column</strong> - Sort by a column (ASC or DESC)</li>
                            <li><strong>LIMIT number</strong> - Limit the number of results</li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">Why Use Python for SQL Tasks?</h3>
                        <ul className="list-disc pl-5 text-gray-600">
                            <li><strong>Data Science & ML Integration</strong> - Seamlessly connect SQL queries to pandas/NumPy data science workflows</li>
                            <li><strong>Advanced Data Processing</strong> - Leverage Python's rich ecosystem for complex data transformations</li>
                            <li><strong>Automation & Scheduling</strong> - Incorporate database queries into automated Python scripts</li>
                            <li><strong>Interactive Data Exploration</strong> - Use in Jupyter notebooks for exploratory analysis</li>
                            <li><strong>Web Application Integration</strong> - Easily incorporate data access in Flask, Django, or FastAPI apps</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SqlToPython;